import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { messageI18n } from '../../../helpers/i18n/toastI18n';
import {
  buildFindingProvenance,
  mergeFindingAdditionalInfo,
  type FindingAdditionalInfo,
} from '../../../helpers/findingsProvenance';
import type { Trimester } from './lib/types';
import { T1Form } from './t1-form';
import { T2Form } from './t2-form';
import { T3Form } from './t3-form';
import { ReportFooterPanel, ReportActions } from './report-footer';
import { AdminStrip } from './admin-strip';
import {
  ReportDataProvider,
  useReportData,
  type PatientInfo,
  type PregnancyInfo,
  type StudyInfo,
  type ReportDataValues,
  type AnatomyStatus,
  type FetalAnatomyItem,
  type ReportDetailMode,
  type ViewImage,
} from './lib/report-context';
import {
  useGetGlobalReportByStudyId,
  useGetStudyReportViewsImages,
  useUpdateStudyReportWithFindings,
  useDeleteStudyReportView,
} from '../../../helpers/api/reports/reports';
import { useUpdatePregnancy } from '../../../helpers/api/pregnancy/pregnancy';
import {
  LuBarChart2,
  LuFlaskConical,
  LuHistory,
  LuRefreshCw,
} from 'react-icons/lu';
import { MdOutlineGridView } from 'react-icons/md';
import { FiEdit3, FiFileText, FiUpload } from 'react-icons/fi';
import { FrameEditorModal } from './frame-editor-modal';
import FetalGrowthCharts from './Fetalgrowthcharts';
import DatingFetalGrowth from './DatingFetalGrowth';
import { ClinicalPrenatalSection } from '../../ComponnentsUI/features/PrenatalExam/ClinicalPrenatalSection';
import { PrenatalMediaUploadForm } from '../../ComponnentsUI/features/PrenatalExam/PrenatalMediaUploadForm';
import {
  getHashForReportSection,
  getReportSectionFromHash,
} from '../../ComponnentsUI/features/PrenatalExam/routing';
import CollaborationDiscussButton from '../Collaboration/CollaborationDiscussButton';
import { buildReportTarget } from '../Collaboration/collaboration-types';

type ReportSection =
  | 'formulaire'
  | 'graphs'
  | 'views'
  | 'Dating/Fetal Growth'
  | 'Prenatal Exam'
  | 'Upload';

const TRIMESTER_FORMS: Record<Trimester, () => JSX.Element> = {
  '1st': T1Form,
  '2nd': T2Form,
  '3rd': T3Form,
};

const SECTION_TABS: {
  id: ReportSection;
  labelKey: string;
  icon: React.ReactNode;
}[] = [
  {
    id: 'formulaire',
    labelKey: 'sections.formulaire',
    icon: <FiFileText size={14} />,
  },
  {
    id: 'Dating/Fetal Growth',
    labelKey: 'sections.datingFetalGrowth',
    icon: <LuHistory size={14} />,
  },
  {
    id: 'views',
    labelKey: 'sections.views',
    icon: <MdOutlineGridView size={14} />,
  },
  {
    id: 'graphs',
    labelKey: 'sections.graphs',
    icon: <LuBarChart2 size={14} />,
  },
  {
    id: 'Prenatal Exam',
    labelKey: 'sections.prenatalExam',
    icon: <LuFlaskConical size={14} />,
  },
  {
    id: 'Upload',
    labelKey: 'sections.upload',
    icon: <FiUpload size={14} />,
  },
];

const CATEGORY_TO_TRIMESTER: Record<string, Trimester> = {
  T1: '1st',
  T2: '2nd',
  T3: '3rd',
};

function getInitialReportSectionFromHash(hash: string): ReportSection | null {
  return getReportSectionFromHash(hash);
}

const REPORT_DETAIL_MODE_STORAGE_KEY = 'deepecho.report.detailMode';

function getStoredReportDetailMode(): ReportDetailMode {
  try {
    return window.localStorage.getItem(REPORT_DETAIL_MODE_STORAGE_KEY) ===
      'full'
      ? 'full'
      : 'minimal';
  } catch {
    return 'minimal';
  }
}

function storeReportDetailMode(mode: ReportDetailMode) {
  try {
    window.localStorage.setItem(REPORT_DETAIL_MODE_STORAGE_KEY, mode);
  } catch {
    // Ignore storage failures; the current in-memory choice still applies.
  }
}

/** Derive trimester from GA weeks. < 14 → T1, 14–27 → T2, ≥ 28 → T3. */
function gaWeeksToTrimester(weeks: number): Trimester {
  if (weeks < 14) {
    return '1st';
  }
  if (weeks < 28) {
    return '2nd';
  }
  return '3rd';
}

/**
 * Trimester detection — based on current pregnancy dates.
 * Priority:
 *   1. LMP  + reference date (study date, or today if absent) → compute GA weeks
 *   2. EDD  + reference date → derive LMP (EDD - 280 days) → compute GA weeks
 *   3. studyInfo.category  (T1 / T2 / T3 from backend study metadata)
 *   4. Default '1st'
 */
function detectTrimester(
  pregnancyInfo?: PregnancyInfo,
  studyInfo?: StudyInfo
): Trimester {
  const refDate = parseCalendarDate(studyInfo?.studyDate) ?? new Date();
  const refTime = startOfDayMs(refDate);

  // 1. LMP → GA
  const lmpStr = pregnancyInfo?.lastMenstrualPeriod;
  const lmp = parseCalendarDate(lmpStr);
  if (lmp) {
    const gaWeeks = (refTime - startOfDayMs(lmp)) / (7 * 24 * 60 * 60 * 1000);
    if (gaWeeks > 0) {
      return gaWeeksToTrimester(Math.floor(gaWeeks));
    }
  }

  // 2. EDD -> derive LMP (EDD - 280 days) -> GA
  const eddStr = pregnancyInfo?.estimatedDueDate;
  const edd = parseCalendarDate(eddStr);
  if (edd) {
    const lmpTime = startOfDayMs(edd) - 280 * 24 * 60 * 60 * 1000;
    const gaWeeks = (refTime - lmpTime) / (7 * 24 * 60 * 60 * 1000);
    if (gaWeeks > 0) {
      return gaWeeksToTrimester(Math.floor(gaWeeks));
    }
  }

  // 3. studyInfo.category (T1 / T2 / T3)
  const fromCategory = CATEGORY_TO_TRIMESTER[studyInfo?.category ?? ''];
  if (fromCategory) {
    return fromCategory;
  }

  // 4. Default
  return '1st';
}

interface ReportFormProps {
  studyId?: string;
  patientInfo?: PatientInfo;
  pregnancyInfo?: PregnancyInfo;
  studyInfo?: StudyInfo;
  isLoadingExamInfo?: boolean;
  hasInstances?: boolean;
}

function roundCm(val?: number): number | undefined {
  if (val === undefined || val === null) {
    return undefined;
  }
  return Math.round(val * 100) / 100;
}

function GaBlock({
  label,
  weeks,
  days,
  accent,
}: {
  label: string;
  weeks?: number;
  days?: number;
  accent: 'white' | 'yellow' | 'blue';
}) {
  const ring =
    accent === 'yellow'
      ? 'ring-amber-400/50'
      : accent === 'blue'
        ? 'ring-blue-400/50'
        : 'ring-white/20';
  const badge =
    accent === 'yellow'
      ? 'bg-amber-50 dark:bg-amber-400/20 text-amber-600 dark:text-amber-300'
      : accent === 'blue'
        ? 'bg-blue-50 dark:bg-blue-400/20 text-blue-600 dark:text-blue-300'
        : 'bg-white/10 text-white';
  const valueClass =
    accent === 'white'
      ? 'w-10 bg-white/20 text-center rounded px-0.5 text-xs text-white border-none outline-none'
      : 'w-10 bg-slate-100 dark:bg-gray-700 text-center rounded px-0.5 text-xs text-slate-700 dark:text-gray-200 border-none outline-none';
  const textColor =
    accent === 'white' ? 'text-white' : 'text-slate-700 dark:text-gray-200';
  const mutedColor =
    accent === 'white'
      ? 'opacity-60 text-white'
      : 'text-slate-400 dark:text-gray-400';
  return (
    <div
      className={`flex flex-row items-center gap-1.5 px-2 py-1 rounded-lg ring-1 ${ring}`}
    >
      {/* Label badge */}
      <span
        className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${badge}`}
      >
        {label}
      </span>
      {/* Values: Xw + Yd */}
      <div
        className={`flex items-center gap-0.5 text-xs font-semibold ${textColor}`}
      >
        <input
          key={weeks}
          type="number"
          defaultValue={weeks ?? 0}
          className={valueClass}
        />
        <span className={`text-[10px] ${mutedColor}`}>w</span>
        <span className={`text-[10px] mx-0.5 ${mutedColor}`}>+</span>
        <input
          key={`d-${days}`}
          type="number"
          defaultValue={days ?? 0}
          min={0}
          max={6}
          className={valueClass}
        />
        <span className={`text-[10px] ${mutedColor}`}>d</span>
      </div>
    </div>
  );
}

function isoToDateInput(raw?: string): string {
  if (!raw || raw === 'Unknown') {
    return '';
  }
  const m = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) {
    return m[1];
  }
  const d = new Date(raw);
  if (isNaN(d.getTime())) {
    return '';
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfDayMs(date: Date): number {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
}

function parseCalendarDate(raw?: string | null): Date | null {
  if (!raw || raw === 'Unknown') {
    return null;
  }
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(raw);
  if (isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function toUtcNoonISOString(date: Date): string {
  return new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0)
  ).toISOString();
}

function gaWeeksToParts(
  gaWeeks?: number | null
): { weeks: number; days: number; totalDays: number } | null {
  if (gaWeeks == null || !Number.isFinite(gaWeeks) || gaWeeks < 0) {
    return null;
  }
  const totalDays = Math.max(0, Math.round(gaWeeks * 7));
  return {
    weeks: Math.floor(totalDays / 7),
    days: totalDays % 7,
    totalDays,
  };
}

function calcGaFromPregnancy(
  studyInfo?: StudyInfo,
  rawLmp?: string
): { weeks: number; days: number } | null {
  const refDate = parseCalendarDate(studyInfo?.studyDate) ?? new Date();
  const refTime = startOfDayMs(refDate);

  const lmpDate = parseCalendarDate(rawLmp);
  if (!lmpDate) {
    return null;
  }

  const lmpTime = startOfDayMs(lmpDate);
  if (refTime <= lmpTime) {
    return null;
  }

  const totalDays = Math.floor((refTime - lmpTime) / (24 * 60 * 60 * 1000));
  return { weeks: Math.floor(totalDays / 7), days: totalDays % 7 };
}

function SectionTabs({
  active,
  trimester,
  onChange,
  studyInfo,
  predGaAua,
  rawLmp,
  rawEdd,
  onSync,
  isSyncing,
  isLocked,
  isFormulaireDisabled,
}: {
  active: ReportSection;
  trimester: Trimester;
  onChange: (s: ReportSection) => void;
  studyInfo?: StudyInfo;
  predGaAua?: number;
  rawLmp?: string;
  rawEdd?: string;
  onSync?: () => void;
  isSyncing?: boolean;
  isLocked?: boolean;
  isFormulaireDisabled?: boolean;
}) {
  const { t } = useTranslation('Report');
  const trimesterLabel: Record<Trimester, string> = {
    '1st': 'T1 — 1st Trimester',
    '2nd': 'T2 — 2nd Trimester',
    '3rd': 'T3 — 3rd Trimester',
  };

  const [lmpInput, setLmpInput] = useState(isoToDateInput(rawLmp));
  const [eddInput, setEddInput] = useState(isoToDateInput(rawEdd));

  useEffect(() => {
    setLmpInput(isoToDateInput(rawLmp));
  }, [rawLmp]);

  useEffect(() => {
    setEddInput(isoToDateInput(rawEdd));
  }, [rawEdd]);

  const calcedPreg = calcGaFromPregnancy(studyInfo, rawLmp);

  const aiGa = gaWeeksToParts(predGaAua);
  const aiGaWeeks = aiGa?.weeks;
  const aiGaDays = aiGa?.days;

  return (
    <nav className="flex items-center justify-between gap-2 px-2 py-1 bg-slate-100 dark:bg-gray-800/50 border-b border-slate-200 dark:border-gray-700 print:hidden">
      {/* ── Section tabs (left) ── */}
      <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
        <div className="flex shrink-0 items-center gap-0.5">
          {SECTION_TABS.map((tab) => {
            const isDisabled =
              tab.id === 'formulaire' && Boolean(isFormulaireDisabled);
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (!isDisabled) {
                    onChange(tab.id);
                  }
                }}
                disabled={isDisabled}
                title={
                  isDisabled
                    ? 'Add an LMP to enable the trimester form'
                    : undefined
                }
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold rounded-lg transition-all duration-200 ${
                  isDisabled
                    ? 'text-slate-300 dark:text-gray-600 cursor-not-allowed'
                    : active === tab.id
                      ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm'
                      : 'text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200'
                }`}
              >
                {tab.icon}
                {tab.id === 'formulaire'
                  ? trimesterLabel[trimester]
                  : t(tab.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── GA blocks + LMP / EDD (right) ── */}
      <div className="flex items-center gap-2.5 text-xs shrink-0">
        {/* Pregnancy record GA */}
        {calcedPreg && (
          <GaBlock
            label="Preg GA"
            weeks={calcedPreg.weeks}
            days={calcedPreg.days}
            accent="blue"
          />
        )}

        {/* AI-predicted GA + Sync button */}
        {predGaAua != null && (
          <>
            {calcedPreg && (
              <span className="text-slate-300 dark:text-gray-600 select-none">
                |
              </span>
            )}
            <div className="flex items-end gap-1.5">
              <GaBlock
                label="Exam GA"
                weeks={aiGaWeeks}
                days={aiGaDays}
                accent="yellow"
              />
              {onSync && (
                <button
                  onClick={onSync}
                  disabled={isSyncing || isLocked}
                  title={
                    isLocked
                      ? 'Report is locked — cannot sync'
                      : 'Sync pregnancy with AI-predicted GA'
                  }
                  className={`flex flex-row items-center gap-0.5 px-2 py-1 rounded-lg ring-1 ring-amber-400/50 transition-all ${
                    isLocked
                      ? 'opacity-30 cursor-not-allowed'
                      : 'bg-amber-50 dark:bg-amber-400/10 hover:bg-amber-100 dark:hover:bg-amber-400/20 cursor-pointer'
                  }`}
                >
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                    Sync
                  </span>
                  <LuRefreshCw
                    size={12}
                    className={`text-amber-500 dark:text-amber-400 ${isSyncing ? 'animate-spin' : ''}`}
                  />
                </button>
              )}
            </div>
          </>
        )}

        {(calcedPreg || predGaAua != null) && (
          <span className="text-slate-300 dark:text-gray-600 select-none">
            |
          </span>
        )}

        {/* LMP */}
        <span className="flex items-center gap-1">
          <span className="text-slate-400 dark:text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
            LMP
          </span>
          <input
            type="date"
            value={lmpInput}
            onChange={(e) => setLmpInput(e.target.value)}
            className="bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded px-1 text-[11px] text-slate-700 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
          />
        </span>

        {/* EDD */}
        <span className="flex items-center gap-1">
          <span className="text-slate-400 dark:text-gray-500 text-[10px] font-semibold uppercase tracking-wider">
            EDD
          </span>
          <input
            type="date"
            value={eddInput}
            onChange={(e) => setEddInput(e.target.value)}
            className="bg-white dark:bg-gray-700 border border-slate-200 dark:border-gray-600 rounded px-1 text-[11px] text-slate-700 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400"
          />
        </span>
      </div>
    </nav>
  );
}

function ViewImagesSection({ studyUID }: { studyUID: string }) {
  const { viewImages, isLoading, onDeleteView, isDeletingView } =
    useReportData();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    frameViewId: string;
    name: string;
  } | null>(null);

  const images = viewImages ?? [];
  const editingView: ViewImage | null =
    editingIndex != null ? (images[editingIndex] ?? null) : null;

  const viewCount = images.length;
  const externalNav = useMemo(
    () =>
      editingIndex != null
        ? {
            total: viewCount,
            currentIndex: editingIndex,
            onNavigate: (index: number) => {
              if (index >= 0 && index < viewCount) {
                setEditingIndex(index);
              }
            },
          }
        : undefined,
    [editingIndex, viewCount]
  );

  if (isLoading && images.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-3 text-slate-400 dark:text-gray-500">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="text-xs font-medium">Loading view images...</p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Section heading */}
      <div className="flex items-center gap-2 mb-4">
        <MdOutlineGridView
          className="text-blue-600 dark:text-blue-400"
          size={16}
        />
        <h2 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
          Captured Views
        </h2>
        <span className="ml-auto text-[10px] text-slate-400 dark:text-gray-500 font-medium">
          {images.length} image{images.length !== 1 ? 's' : ''}
        </span>
      </div>

      {images.length > 0 ? (
        <div className="grid grid-cols-2 gap-4">
          {images.map((img, i) => (
            <div key={img.frame_id || i} className="group flex flex-col">
              {/* Image card */}
              <div className="relative bg-slate-100 dark:bg-gray-700/60 border border-slate-200 dark:border-gray-600 rounded-xl overflow-hidden">
                <div
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer"
                  onClick={() => setEditingIndex(i)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setEditingIndex(i);
                    }
                  }}
                  aria-label={`Edit ${img.display_name}`}
                >
                  <img
                    src={`data:image/jpeg;base64,${img.image_base64}`}
                    alt={img.display_name}
                    className="w-full object-contain max-h-56"
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center pointer-events-none">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 dark:bg-gray-800/90 rounded-full p-2 shadow-lg">
                      <FiEdit3
                        size={18}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    </div>
                  </div>
                </div>
                {/* Delete button */}
                {onDeleteView && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete({
                        frameViewId: img.frame_id,
                        name: img.display_name,
                      });
                    }}
                    className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-red-500/80 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                    title="Delete view"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                )}
              </div>
              {/* Caption */}
              <p className="text-center text-[11px] font-medium text-slate-600 dark:text-gray-400 mt-2 truncate px-1">
                {img.display_name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-48 gap-3 text-slate-400 dark:text-gray-500">
          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-gray-700/50 flex items-center justify-center">
            <MdOutlineGridView size={22} className="opacity-40" />
          </div>
          <p className="text-xs font-medium">
            No view images captured for this study
          </p>
        </div>
      )}

      {/* Exact-frame segmentation editor. It fetches the source image and current
          segmentations through the same APIs as the viewer; the base64 report image
          remains a thumbnail only. Restricted to the clicked frame: no other image of
          the instance is fetched, only this view's segmentations are shown, and
          prev/next cycles through the captured views (not DICOM frames). */}
      {editingView && editingView.sop_instance_uid && studyUID && (
        <FrameEditorModal
          studyUID={studyUID}
          target={editingView}
          subtitle="Edit segmentations and save your changes"
          nav={externalNav}
          onClose={() => setEditingIndex(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-red-600 dark:text-red-400"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-gray-100">
                  Delete View
                </h3>
                <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            <p className="text-sm text-slate-600 dark:text-gray-300 mb-5">
              Are you sure you want to delete{' '}
              <span className="font-semibold">
                &ldquo;{confirmDelete.name}&rdquo;
              </span>
              ?
            </p>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-xs font-medium rounded-lg border border-slate-300 dark:border-gray-600 text-slate-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  onDeleteView?.(confirmDelete.frameViewId);
                  setConfirmDelete(null);
                }}
                disabled={isDeletingView}
                className="px-4 py-2 text-xs font-medium rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
              >
                {isDeletingView ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReportForm({
  studyId,
  patientInfo,
  pregnancyInfo,
  studyInfo,
  isLoadingExamInfo,
  hasInstances = true,
}: ReportFormProps) {
  const { t } = useTranslation(['Report', 'Patient', 'ClinicalCollaboration']);
  const location = useLocation();
  const navigate = useNavigate();
  const initialSectionFromHash = getInitialReportSectionFromHash(location.hash);
  const defaultSection: ReportSection = hasInstances
    ? 'formulaire'
    : 'Prenatal Exam';
  const [trimester, setTrimester] = useState<Trimester>(() =>
    detectTrimester(pregnancyInfo, studyInfo)
  );
  const [activeSection, setActiveSection] = useState<ReportSection>(
    initialSectionFromHash ?? defaultSection
  );
  const [reportDetailMode, setReportDetailMode] = useState<ReportDetailMode>(
    getStoredReportDetailMode
  );

  useEffect(() => {
    const sectionFromHash = getInitialReportSectionFromHash(location.hash);
    if (sectionFromHash) {
      setActiveSection(sectionFromHash);
    }
  }, [location.hash]);

  const handleReportDetailModeChange = useCallback((mode: ReportDetailMode) => {
    setReportDetailMode(mode);
    storeReportDetailMode(mode);
  }, []);

  const resolvedStudyId = studyId ?? studyInfo?.studyId ?? '';

  const { data: globalReportRaw, isLoading: isLoadingReport } =
    useGetGlobalReportByStudyId(resolvedStudyId, [
      `globalReportData/${resolvedStudyId}`,
    ]) as { data: any; isLoading: boolean };

  const { data: viewImagesRaw, isLoading: isLoadingViewImages } =
    useGetStudyReportViewsImages(resolvedStudyId, activeSection === 'views', [
      `viewImages/${resolvedStudyId}`,
    ]) as { data: any; isLoading: boolean };

  const queryClient = useQueryClient();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncedLmp, setSyncedLmp] = useState<string | undefined>(undefined);
  const [syncedEdd, setSyncedEdd] = useState<string | undefined>(undefined);

  const dataReport = globalReportRaw?.data || {};
  const studyReport = dataReport?.current_study_report;
  const clinicalImpression = dataReport?.clinical_impression;
  const isLocked = dataReport?.current_study_info?.is_locked === true;
  const patientId: string | undefined =
    dataReport?.patient_info?.id ??
    dataReport?.current_pregnancy?.patient_id ??
    undefined;
  const currentPregnancyId: number | undefined =
    dataReport?.current_pregnancy?.id ?? undefined;
  const rawLmp: string | undefined =
    syncedLmp ?? dataReport?.current_pregnancy?.last_menstrual_period;
  const rawEdd: string | undefined =
    syncedEdd ?? dataReport?.current_pregnancy?.estimated_due_date;
  const prenatalStudyDate =
    studyReport?.study_date ??
    dataReport?.current_study_info?.study_date ??
    studyInfo?.studyDate ??
    null;
  const effectiveStudyInfo = useMemo(
    () =>
      studyInfo && prenatalStudyDate
        ? { ...studyInfo, studyDate: prenatalStudyDate }
        : studyInfo,
    [prenatalStudyDate, studyInfo]
  );
  const lmp = pregnancyInfo?.lastMenstrualPeriod;
  const edd = pregnancyInfo?.estimatedDueDate;
  const studyDate = effectiveStudyInfo?.studyDate;
  const category = effectiveStudyInfo?.category;

  useEffect(() => {
    setTrimester(detectTrimester(pregnancyInfo, effectiveStudyInfo));
  }, [category, edd, effectiveStudyInfo, lmp, studyDate]);

  const hasCurrentLmp = Boolean(parseCalendarDate(rawLmp));
  const isFormulaireDisabled = !hasCurrentLmp;
  const computedGa = calcGaFromPregnancy(effectiveStudyInfo, rawLmp);
  const currentGaWeeks: number | undefined = computedGa?.weeks;
  const currentGaDays: number | undefined = computedGa?.days;

  useEffect(() => {
    if (
      hasInstances ||
      !isFormulaireDisabled ||
      activeSection !== 'formulaire'
    ) {
      return;
    }

    const fallbackSection: ReportSection = 'Prenatal Exam';
    const nextHash = getHashForReportSection(fallbackSection);
    setActiveSection(fallbackSection);
    if (location.hash !== nextHash) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
        },
        { replace: true }
      );
    }
  }, [
    activeSection,
    hasInstances,
    isFormulaireDisabled,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const reportQueryKey = useMemo(
    () => [
      `/study_report/views/${resolvedStudyId}`,
      undefined,
      [`globalReportData/${resolvedStudyId}`],
    ],
    [resolvedStudyId]
  );

  const updatePregnancyInCache = useCallback(
    (pregnancyData: Record<string, unknown>) => {
      queryClient.setQueryData(reportQueryKey, (old: any) => {
        if (!old?.data) {
          return old;
        }
        return {
          ...old,
          data: {
            ...old.data,
            current_pregnancy: {
              ...old.data.current_pregnancy,
              ...pregnancyData,
            },
          },
        };
      });
    },
    [queryClient, reportQueryKey]
  );

  const handlePregnancyModalUpdate = useCallback(
    (updatedPregnancy: Record<string, unknown>) => {
      updatePregnancyInCache(updatedPregnancy);

      const newLmp = updatedPregnancy.last_menstrual_period as
        | string
        | undefined;
      const newEdd = updatedPregnancy.estimated_due_date as string | undefined;

      const ga = calcGaFromPregnancy(effectiveStudyInfo, newLmp);
      if (ga && ga.weeks > 0) {
        setTrimester(gaWeeksToTrimester(ga.weeks));
      }

      if (newLmp) {
        setSyncedLmp(newLmp);
      }
      if (newEdd) {
        setSyncedEdd(newEdd);
      }
    },
    [effectiveStudyInfo, updatePregnancyInCache, setTrimester]
  );

  const { mutate: updatePregnancy } = useUpdatePregnancy(
    (response: any) => {
      messageI18n.success('report.pregnancySyncedAi');
      setIsSyncing(false);
      const updatedPregnancy: Record<string, unknown> =
        (response?.data as Record<string, unknown>) ?? response;
      updatePregnancyInCache(updatedPregnancy);
    },
    () => {
      messageI18n.error('report.pregnancySyncFailed');
      setIsSyncing(false);
      setSyncedLmp(undefined);
      setSyncedEdd(undefined);
    }
  );

  const handleSyncPregnancy = () => {
    const currentStudyReport = dataReport?.current_study_report;
    const currentPregnancy = dataReport?.current_pregnancy;
    const currentStudyInfo = dataReport?.current_study_info;

    if (!currentStudyReport || !currentPregnancy || !currentStudyInfo) {
      messageI18n.error('report.missingPregnancyData');
      return;
    }
    const currentMeasurements = currentStudyReport.measurements;
    if (currentMeasurements?.ga_aua == null) {
      messageI18n.warning('report.noGestationalAgePrediction');
      return;
    }

    setIsSyncing(true);

    const rawStudyDate =
      currentStudyInfo.study_date || effectiveStudyInfo?.studyDate || null;
    const baseDate = parseCalendarDate(rawStudyDate) ?? new Date();
    const baseDayMs = startOfDayMs(baseDate);
    const gaParts = gaWeeksToParts(currentMeasurements.ga_aua);
    if (!gaParts) {
      messageI18n.warning('report.noGestationalAgePrediction');
      setIsSyncing(false);
      return;
    }
    const lmpDate = new Date(
      baseDayMs - gaParts.totalDays * 24 * 60 * 60 * 1000
    );
    const eddDate = new Date(lmpDate.getTime() + 280 * 24 * 60 * 60 * 1000);
    const syncedLmpIso = toUtcNoonISOString(lmpDate);
    const syncedEddIso = toUtcNoonISOString(eddDate);
    const referenceDateIso = toUtcNoonISOString(baseDate);

    setSyncedLmp(syncedLmpIso);
    setSyncedEdd(syncedEddIso);
    setTrimester(gaWeeksToTrimester(gaParts.weeks));

    updatePregnancy({
      id: currentPregnancy.id,
      patient_id: currentPregnancy.patient_id,
      last_menstrual_period: syncedLmpIso,
      estimated_due_date: syncedEddIso,
      study_date: referenceDateIso,
      ultrasound_date: referenceDateIso,
      dating_method: 'ULTRASOUND',
      pregnancy_status: currentPregnancy.pregnancy_status,
    });
  };

  const { mutateAsync: updateStudyReport, isPending: isSavingReport } =
    useUpdateStudyReportWithFindings(
      (response: any) => {
        const serverReport = response;
        if (!serverReport) {
          return;
        }

        queryClient.setQueryData(reportQueryKey, (old: any) => {
          if (!old?.data?.current_study_report) {
            return old;
          }
          const updated: any = {
            ...old,
            data: {
              ...old.data,
              current_study_report: {
                ...old.data.current_study_report,
                ...serverReport,
                measurements: {
                  ...old.data.current_study_report.measurements,
                  ...serverReport.measurements,
                },
              },
            },
          };
          if (Array.isArray(serverReport.fetal_anatomy)) {
            updated.data.fetal_anatomy = serverReport.fetal_anatomy;
          }
          return updated;
        });
      },
      () => {
        messageI18n.error('report.updateFailed');
      }
    );

  const viewImagesQueryKey = useMemo(
    () => [
      `/study_report/views/images/${resolvedStudyId}`,
      undefined,
      [`viewImages/${resolvedStudyId}`],
    ],
    [resolvedStudyId]
  );

  const { mutate: deleteView, isPending: isDeletingView } =
    useDeleteStudyReportView(
      () => {
        queryClient.invalidateQueries({ queryKey: viewImagesQueryKey });
      },
      () => {
        messageI18n.error('report.deleteViewFailed');
      }
    );

  const handleDeleteView = useCallback(
    (frameViewId: string) => {
      queryClient.setQueryData(viewImagesQueryKey, (old: any) => {
        if (!old?.data?.views) {
          return old;
        }
        return {
          ...old,
          data: {
            ...old.data,
            views: old.data.views.filter(
              (v: any) => v.frame_id !== frameViewId
            ),
          },
        };
      });

      deleteView({
        studyInstanceUid: resolvedStudyId,
        frameViewId,
      });
    },
    [queryClient, viewImagesQueryKey, resolvedStudyId, deleteView]
  );

  const handleUpdateFindings = useCallback(
    async (updates: Record<string, unknown>) => {
      const previousReportQueryData = queryClient.getQueryData(reportQueryKey);
      const MEASUREMENT_KEYS = new Set([
        'crl_cm',
        'nt_cm',
        'nt_mom',
        'nasal_bone_t1_present',
        'hard_palate_t1_intact',
        'bpd_cm',
        'bpd_percentile',
        'hc_cm',
        'hc_percentile',
        'lateral_ventricle_cm',
        'choroid_plexus_cm',
        'csp_width_cm',
        'tcd_cm',
        'tcd_percentile',
        'cisterna_magna_cm',
        'vermis_height_cm',
        'third_ventricle_cm',
        'corpus_callosum',
        'ctr',
        'lv_diameter_cm',
        'rv_diameter_cm',
        'lv_rv_ratio',
        'ivs_thickness_cm',
        'aorta_diameter_cm',
        'pa_diameter_cm',
        'nasal_bone_t2_cm',
        'orbital_diameter_cm',
        'iod_cm',
        'ac_cm',
        'ac_percentile',
        'fl_cm',
        'fl_percentile',
        'fl_ac_ratio',
        'hl_cm',
        'tibia_cm',
        'fibula_cm',
        'radius_cm',
        'ulna_cm',
        'foot_length_cm',
        'stomach_diameter_cm',
        'transversal_cm',
        'lhr',
        'renal_pelvis_ap_cm',
        'bladder_diameter_cm',
        'afi_cm',
        'mvp_cm',
        'cervical_length_cm',
        'placental_thickness_cm',
        'placenta_distance_to_os_cm',
        'ua_pi',
        'ua_pi_percentile',
        'ua_ri',
        'uta_right_pi',
        'uta_left_pi',
        'uta_mean_pi',
        'mca_pi',
        'mca_pi_percentile',
        'mca_vmax_cms',
        'mca_vmax_mom',
        'cpr',
        'cpr_percentile',
        'dv_pi',
        'dv_pi_percentile',
        'funneling_pct',
        'efw_g',
        'efw_percentile',
        'efw_formula',
        'efw_reference_chart',
        'efw_hadlock_i_g',
        'efw_hadlock_ii_g',
        'efw_hadlock_iii_g',
        'efw_hadlock_iv_g',
        'efw_who_2017_g',
        'hl_percentile',
        'ga_bpd',
        'ga_hc',
        'ga_ac',
        'ga_fl',
        'ga_aua',
        'ac_velocity_cm_2wks',
        'efw_velocity_g_week',
      ]);

      const findingProvenance = buildFindingProvenance(Object.keys(updates), 'manual');

      queryClient.setQueryData(reportQueryKey, (old: any) => {
        if (!old?.data?.current_study_report) {
          return old;
        }

        const measurementUpdates: Record<string, unknown> = {};
        const reportUpdates: Record<string, unknown> = {};
        const studyInfoUpdates: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(updates)) {
          if (MEASUREMENT_KEYS.has(key)) {
            measurementUpdates[key] = value;
          } else {
            reportUpdates[key] = value;
          }

          if (key === 'study_date') {
            studyInfoUpdates.study_date = value;
          }
        }

        const mergedAdditionalInfo = findingProvenance
          ? mergeFindingAdditionalInfo(
              old.data.current_study_report
                .additional_info as FindingAdditionalInfo | null,
              findingProvenance
            )
          : old.data.current_study_report.additional_info;

        return {
          ...old,
          data: {
            ...old.data,
            current_study_report: {
              ...old.data.current_study_report,
              ...reportUpdates,
              ...(mergedAdditionalInfo
                ? { additional_info: mergedAdditionalInfo }
                : {}),
              ...(Object.keys(measurementUpdates).length > 0
                ? {
                    measurements: {
                      ...old.data.current_study_report.measurements,
                      ...measurementUpdates,
                    },
                  }
                : {}),
            },
            current_study_info: {
              ...old.data.current_study_info,
              ...studyInfoUpdates,
            },
          },
        };
      });

      try {
        await updateStudyReport({
          study_instance_uid: resolvedStudyId,
          ...updates,
          ...(findingProvenance ? { additional_info: findingProvenance } : {}),
        });
      } catch (error) {
        queryClient.setQueryData(reportQueryKey, previousReportQueryData);
        throw error;
      }
    },
    [queryClient, reportQueryKey, resolvedStudyId, updateStudyReport]
  );

  const measurements = studyReport?.measurements;

  const biometryValues = useMemo<Record<string, number | undefined>>(() => {
    const vals: Record<string, number | undefined> = {};

    if (measurements) {
      vals.bpd = roundCm(measurements.bpd_cm);
      vals.hc = roundCm(measurements.hc_cm);
      vals.ac = roundCm(measurements.ac_cm);
      vals.fl = roundCm(measurements.fl_cm);
      vals.crl = roundCm(measurements.crl_cm);
      vals.tcd = roundCm(measurements.tcd_cm);
      vals.cerebellum = roundCm(measurements.tcd_cm);
      vals.hl = roundCm(measurements.hl_cm);
      vals.tibia = roundCm(measurements.tibia_cm);
      vals.fibula = roundCm(measurements.fibula_cm);
      vals.radius = roundCm(measurements.radius_cm);
      vals.ulna = roundCm(measurements.ulna_cm);
      vals.foot = roundCm(measurements.foot_length_cm);
      vals.iod = roundCm(measurements.iod_cm);
      vals.nasal = roundCm(measurements.nasal_bone_t2_cm);
      vals['lat-vent'] = roundCm(measurements.lateral_ventricle_cm);
      vals.csp = roundCm(measurements.csp_width_cm);
      vals.cisterna = roundCm(measurements.cisterna_magna_cm);
      vals['3rd-vent'] = roundCm(measurements.third_ventricle_cm);
      vals.vermis = roundCm(measurements.vermis_height_cm);
      vals.choroid = roundCm(measurements.choroid_plexus_cm);
    }

    if (pregnancyInfo) {
      if (!vals.bpd && pregnancyInfo.preg_bpd) {
        vals.bpd = roundCm(pregnancyInfo.preg_bpd);
      }
      if (!vals.hc && pregnancyInfo.preg_hc) {
        vals.hc = roundCm(pregnancyInfo.preg_hc);
      }
      if (!vals.ac && pregnancyInfo.preg_ac) {
        vals.ac = roundCm(pregnancyInfo.preg_ac);
      }
      if (!vals.fl && pregnancyInfo.preg_fl) {
        vals.fl = roundCm(pregnancyInfo.preg_fl);
      }
      if (!vals.crl && pregnancyInfo.preg_crl) {
        vals.crl = roundCm(pregnancyInfo.preg_crl);
      }
    }

    return vals;
  }, [measurements, pregnancyInfo]);

  const rawViewImages = viewImagesRaw?.data?.views;

  const fetalAnatomy = useMemo<FetalAnatomyItem[]>(
    () =>
      Array.isArray(dataReport?.fetal_anatomy) ? dataReport.fetal_anatomy : [],
    [dataReport?.fetal_anatomy]
  );

  const handleAnatomyStatusOverride = useCallback(
    (
      categoryCode: string,
      newStatus: AnatomyStatus,
      fallbackItem?: FetalAnatomyItem
    ) => {
      const target =
        fetalAnatomy.find((i) => i.category_code === categoryCode) ??
        fallbackItem;
      if (!target) {
        return;
      }

      const updatedItem = {
        ...target,
        status: newStatus,
        source: (newStatus === 'visualized'
          ? 'manual'
          : null) as typeof target.source,
      };

      queryClient.setQueryData(reportQueryKey, (old: any) => {
        if (!old?.data) {
          return old;
        }
        const existingAnatomy = Array.isArray(old.data.fetal_anatomy)
          ? old.data.fetal_anatomy
          : [];
        const hasExistingItem = existingAnatomy.some(
          (item: FetalAnatomyItem) => item.category_code === categoryCode
        );
        const nextAnatomy = hasExistingItem
          ? existingAnatomy.map((item: FetalAnatomyItem) =>
              item.category_code === categoryCode ? updatedItem : item
            )
          : [...existingAnatomy, updatedItem];

        return {
          ...old,
          data: {
            ...old.data,
            fetal_anatomy: nextAnatomy,
          },
        };
      });

      updateStudyReport({
        study_instance_uid: resolvedStudyId,
        fetal_anatomy: [updatedItem],
      });
    },
    [
      fetalAnatomy,
      queryClient,
      reportQueryKey,
      resolvedStudyId,
      updateStudyReport,
    ]
  );

  const reportContextValue = useMemo<ReportDataValues>(
    () => ({
      patientInfo,
      pregnancyInfo,
      studyInfo: effectiveStudyInfo,
      studyUID: resolvedStudyId,
      viewImages: Array.isArray(rawViewImages) ? rawViewImages : [],
      measurementSources: Array.isArray(dataReport?.measurement_sources)
        ? dataReport.measurement_sources
        : [],
      additionalInfo:
        studyReport?.additional_info &&
        typeof studyReport.additional_info === 'object'
          ? (studyReport.additional_info as FindingAdditionalInfo)
          : undefined,
      isLoading:
        isLoadingExamInfo ||
        isLoadingReport ||
        (activeSection === 'views' && isLoadingViewImages),
      biometryValues,
      measurementValues: measurements ?? {},
      studyReportFields: studyReport ?? {},
      fetalAssessment: {
        presentation: studyReport?.presentation,
        facing: studyReport?.facing,
        heartRate: studyReport?.heart_rate
          ? Number(studyReport.heart_rate)
          : undefined,
        placentaLocalization: studyReport?.placenta_localization,
        placentaCervixClass: studyReport?.placenta_distance_to_cervix_class,
      },
      efw: {
        efwGrams: measurements?.efw_g ? Number(measurements.efw_g) : undefined,
      },
      clinicalImpression: {
        findings: clinicalImpression?.findings,
        impression: clinicalImpression?.impression,
        recommendations: clinicalImpression?.recommendations,
      },
      screening: {
        gestationalDiabetes: studyReport?.gestational_diabetes ?? null,
        preEclampsia: studyReport?.pre_eclampsia ?? null,
        sexPrediction: studyReport?.sex_prediction ?? null,
        isTwins: studyReport?.is_twins ?? null,
      },
      gaPredictions: {
        predGaBpd: measurements?.ga_bpd
          ? Number(measurements.ga_bpd)
          : undefined,
        predGaHc: measurements?.ga_hc ? Number(measurements.ga_hc) : undefined,
        predGaAc: measurements?.ga_ac ? Number(measurements.ga_ac) : undefined,
        predGaFl: measurements?.ga_fl ? Number(measurements.ga_fl) : undefined,
        predGaAua: measurements?.ga_aua
          ? Number(measurements.ga_aua)
          : undefined,
        predEdd: undefined,
      },
      pregnancyStudiesHistory: Array.isArray(
        dataReport?.pregnancy_studies_history
      )
        ? dataReport.pregnancy_studies_history
        : [],
      fetalAnatomy,
      onAnatomyStatusOverride: handleAnatomyStatusOverride,
      reportDetailMode,
      onReportDetailModeChange: handleReportDetailModeChange,

      onUpdateFindings: handleUpdateFindings,
      isSaving: isSavingReport,
      isLocked,

      patientId,
      onPregnancyUpdate: handlePregnancyModalUpdate,

      currentPregnancyId,
      currentLmp: rawLmp,
      currentEdd: rawEdd,
      currentGaWeeks,
      currentGaDays,

      onDeleteView: handleDeleteView,
      isDeletingView,
    }),
    [
      patientInfo,
      pregnancyInfo,
      effectiveStudyInfo,
      resolvedStudyId,
      rawViewImages,
      dataReport?.measurement_sources,
      isLoadingExamInfo,
      isLoadingReport,
      isLoadingViewImages,
      activeSection,
      biometryValues,
      studyReport,
      measurements,
      clinicalImpression,
      dataReport?.pregnancy_studies_history,
      fetalAnatomy,
      handleAnatomyStatusOverride,
      reportDetailMode,
      handleReportDetailModeChange,
      handleUpdateFindings,
      isSavingReport,
      isLocked,
      patientId,
      handlePregnancyModalUpdate,
      currentPregnancyId,
      rawLmp,
      rawEdd,
      currentGaWeeks,
      currentGaDays,
      handleDeleteView,
      isDeletingView,
    ]
  );

  const ActiveForm = TRIMESTER_FORMS[trimester];

  if (isLoadingReport && !dataReport?.current_study_report) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50 dark:bg-gray-900 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Loading report data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <ReportDataProvider value={reportContextValue}>
      <div className="h-full bg-slate-50 dark:bg-gray-900 print:bg-white flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden print:py-0 print:px-2">
          {/* Card body */}
          <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-sm overflow-hidden">
            {/* Section tabs + GA info bar */}
            {studyReport?.id && (
              <div className="flex items-center justify-end gap-2 border-b border-slate-200 px-4 py-2 dark:border-gray-700">
                <span className="mr-auto text-xs text-slate-500 dark:text-gray-400">
                  {t('reportDiscussHint', { ns: 'ClinicalCollaboration' })}
                </span>
                <CollaborationDiscussButton
                  variant="compact"
                  target={buildReportTarget(studyReport.id)}
                />
              </div>
            )}

            <SectionTabs
              active={activeSection}
              trimester={trimester}
              onChange={(section) => {
                if (section === 'formulaire' && isFormulaireDisabled) {
                  return;
                }
                setActiveSection(section);
                const nextHash = getHashForReportSection(section);
                if (location.hash !== nextHash) {
                  navigate(
                    {
                      pathname: location.pathname,
                      search: location.search,
                      hash: nextHash,
                    },
                    { replace: true }
                  );
                }
              }}
              studyInfo={effectiveStudyInfo}
              predGaAua={measurements?.ga_aua}
              rawLmp={rawLmp}
              rawEdd={rawEdd}
              onSync={handleSyncPregnancy}
              isSyncing={isSyncing}
              isLocked={isLocked}
              isFormulaireDisabled={isFormulaireDisabled}
            />

            {/* Three-column body — all columns always visible */}
            <div className="flex-1 flex overflow-hidden min-h-0">
              {/* LEFT: Admin strip */}
              <div className="shrink-0 border-r border-slate-200 dark:border-gray-700 overflow-y-auto">
                <AdminStrip />
              </div>

              {/* MIDDLE: active section content + comments below */}
              <div className="flex-1 overflow-y-auto flex flex-col">
                {activeSection === 'formulaire' && (
                  <div className="p-3">
                    <ActiveForm />
                  </div>
                )}
                {activeSection === 'graphs' && <FetalGrowthCharts />}
                {activeSection === 'views' && (
                  <ViewImagesSection studyUID={resolvedStudyId} />
                )}
                {activeSection === 'Dating/Fetal Growth' && (
                  <DatingFetalGrowth />
                )}
                {activeSection === 'Prenatal Exam' && (
                  <div className="flex-1 overflow-y-auto p-4">
                    {resolvedStudyId ? (
                      <ClinicalPrenatalSection
                        studyInstanceUid={resolvedStudyId}
                        studyDate={prenatalStudyDate}
                        readOnly={Boolean(isLocked)}
                        initialValues={studyReport ?? null}
                        onSubmitExisting={handleUpdateFindings}
                        isSubmitting={isSavingReport}
                        formVersion={studyReport?.updated_at ?? resolvedStudyId}
                      />
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-card-color-100 p-5 text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-gray-800 dark:text-slate-300">
                        {t('details_dossier_loading')}
                      </div>
                    )}
                  </div>
                )}
                {activeSection === 'Upload' && (
                  <div className="flex-1 overflow-y-auto p-4">
                    {resolvedStudyId ? (
                      isLocked ? (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                          {t('prenatalExam.upload.lockedHint', { ns: 'Patient' })}
                        </div>
                      ) : (
                        <PrenatalMediaUploadForm
                          studyInstanceUid={resolvedStudyId}
                          patientId={patientId ?? ''}
                          pregnancyMeta={{
                            trimesterLabel:
                              trimester === '1st'
                                ? 'T1'
                                : trimester === '2nd'
                                  ? 'T2'
                                  : 'T3',
                            gestationalAgeLabel:
                              currentGaWeeks != null
                                ? `${currentGaWeeks}+${currentGaDays ?? 0}`
                                : null,
                            edd: rawEdd ?? null,
                            studyDate: prenatalStudyDate,
                          }}
                        />
                      )
                    ) : (
                      <div className="rounded-2xl border border-slate-200 bg-card-color-100 p-5 text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-gray-800 dark:text-slate-300">
                        {t('details_dossier_loading')}
                      </div>
                    )}
                  </div>
                )}

                {/* Comments / Conclusion / Management Plan */}
                {activeSection === 'formulaire' && <ReportActions />}
              </div>

              {/* RIGHT: action buttons */}
              <div className="shrink-0 w-44 border-l border-slate-200 dark:border-gray-700 overflow-y-auto">
                <ReportFooterPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReportDataProvider>
  );
}
